import { apiRequest } from './http';

export interface PurchasePayload {
    plan_name: string;
    price: number;
}

export interface PurchaseResponse {
    message?: string;
    data?: any;
    [key: string]: any;
}

export interface CreatePaymentSessionPayload {
    plan_id?: number;
    plan_name: string;
    amount: number;
    first_name: string;
    last_name: string;
    email: string;
    country: string;
    address?: string;
    city?: string;
    postal_code?: string;
    phone?: string;
}

export interface CreatePaymentSessionResponse {
    success?: boolean;
    message?: string;
    data?: {
        sessionToken: string;
    };
}

export interface ValidateIbanPayload {
    session_token: string;
    iban: string;
}

export interface ValidateIbanResponse {
    success?: boolean;
    message?: string;
    data?: {
        valid: boolean;
        bankName: string | null;
        bic: string | null;
        message: string | null;
    };
}

export interface SubmitPaymentPayload {
    session_token: string;
    iban: string;
    iban_holder_name: string;
}

export interface VerifyPaymentPayload {
    session_token: string;
    verification_id?: string;
}

export interface SubmitPaymentResponse {
    success?: boolean;
    message?: string;
    data?: {
        sessionToken: string;
        status: string;
        retryAllowed: boolean;
        verificationUrl: string | null;
        message: string | null;
        subscriptionActive: boolean;
    };
}

export const purchaseSubscription = async (
    token: string,
    payload: PurchasePayload
): Promise<PurchaseResponse> => {
    return apiRequest<PurchaseResponse>({
        endpoint: '/subscriptions/purchase',
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    });
};

export const createPaymentSession = async (
    token: string,
    payload: CreatePaymentSessionPayload
): Promise<CreatePaymentSessionResponse> => {
    return apiRequest<CreatePaymentSessionResponse>({
        endpoint: '/subscriptions/create-payment-session',
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    });
};

export const validateIban = async (
    token: string | null,
    payload: ValidateIbanPayload
): Promise<ValidateIbanResponse> => {
    return apiRequest<ValidateIbanResponse>({
        endpoint: '/subscriptions/validate-iban',
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
    });
};

export const submitPayment = async (
    token: string,
    payload: SubmitPaymentPayload
): Promise<SubmitPaymentResponse> => {
    return apiRequest<SubmitPaymentResponse>({
        endpoint: '/subscriptions/submit-payment',
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    });
};

export const verifyPayment = async (
    token: string,
    payload: VerifyPaymentPayload
): Promise<SubmitPaymentResponse> => {
    return apiRequest<SubmitPaymentResponse>({
        endpoint: '/subscriptions/verify-payment',
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    });
};

export interface UnsubscribePayload {
    first_name: string;
    last_name: string;
    email: string;
    iban_last_5: string;
}

export interface UnsubscribeResponse {
    message?: string;
    data?: any;
}

export const unsubscribe = async (
    payload: UnsubscribePayload
): Promise<UnsubscribeResponse> => {
    return apiRequest<UnsubscribeResponse>({
        endpoint: '/subscriptions/unsubscribe',
        method: 'POST',
        body: JSON.stringify(payload),
    });
};
